/**
 * ChatModalsLayer — the chat-level modal cluster (OAuthConsentModal,
 * UsageLimitModal, ContactCardPopover, NoteCardPopover, DocEditorModal). The
 * orchestrator owns all state and threads it as props, so render timing and the
 * UsageLimitModal analytics side effects (console.info + CustomEvent) are
 * unchanged. The console mirror uses console.info (not debug) so the prod
 * build's console.log/debug stripping (#5264) leaves this channel intact.
 */
import { Suspense, useEffect, useState } from 'react';
import { OAuthConsentModal, type OAuthConsentProvider } from '@/features/oauth/components/OAuthConsentModal';
import { UsageLimitModal, type UsageLimitMode } from '@/common/components/UsageLimitModal';
import { NoteCardPopover } from '@/features/chat/components/NoteCardPopover';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import type { DocForEditor } from '@/modules/drive/docs/DocEditorModal';
import { http } from '@/lib/http';
import { useSession } from '@/features/auth/hooks/useSession';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';

// Open-on-demand modals kept out of the entry chunk: DocEditorModal pulls the
// full TipTap/ProseMirror stack and ContactCardPopover pulls ContactEditModal.
const ContactCardPopover = lazyWithRetry(() =>
  import('@/modules/contacts/components/ContactCardPopover').then((m) => ({ default: m.ContactCardPopover })),
);
const DocEditorModal = lazyWithRetry(() => import('@/modules/drive/docs/DocEditorModal'));

export interface ChatModalsLayerProps {
  // OAuth consent interstitial
  pendingOAuthConsent: { provider: OAuthConsentProvider; resolve: (ok: boolean) => void } | null;
  setPendingOAuthConsent: (value: { provider: OAuthConsentProvider; resolve: (ok: boolean) => void } | null) => void;
  // Usage / storage limit modal
  storageExceeded: boolean;
  setStorageExceeded: (value: boolean) => void;
  limitState: {
    open: boolean;
    mode: UsageLimitMode;
    resetsAt?: string;
    retryAfterSec?: number;
    errorCode?: string;
  };
  setLimitDismissed: (value: boolean) => void;
  // Object/note card popovers
  objectCardPopover: { objectType: string; objectId: string } | null;
  setObjectCardPopover: (value: { objectType: string; objectId: string } | null) => void;
  noteModal: { noteId: string } | null;
  setNoteModal: (value: { noteId: string } | null) => void;
  docModal: { docId: string } | null;
  setDocModal: (value: { docId: string } | null) => void;
  applyUiActions: (actions: ChatUIAction[]) => void;
  activeConversationId?: string;
}

export function ChatModalsLayer({
  pendingOAuthConsent,
  setPendingOAuthConsent,
  storageExceeded,
  setStorageExceeded,
  limitState,
  setLimitDismissed,
  objectCardPopover,
  setObjectCardPopover,
  noteModal,
  setNoteModal,
  docModal,
  setDocModal,
  applyUiActions,
  activeConversationId,
}: ChatModalsLayerProps) {
  const { token } = useSession();
  const [docForEditor, setDocForEditor] = useState<DocForEditor | null>(null);

  useEffect(() => {
    if (!docModal) { setDocForEditor(null); return; }
    void (async () => {
      try {
        const res = await http.get<any>(`/notes/${docModal.docId}`, { authToken: token ?? undefined });
        if (res.data) {
          setDocForEditor({
            id: res.data.id,
            title: res.data.title || 'Untitled',
            body: res.data.body || res.data.content || '',
            _etag: res.etag || res.data._etag,
            authorId: res.data.authorId,
            checkpoints: res.data.checkpoints,
          });
        } else {
          setDocModal(null);
        }
      } catch {
        setDocModal(null);
      }
    })();
  }, [docModal, token, setDocModal]);
  return (
    <>
      <OAuthConsentModal
        open={pendingOAuthConsent !== null}
        provider={pendingOAuthConsent?.provider ?? null}
        onConfirm={() => {
          pendingOAuthConsent?.resolve(true);
          setPendingOAuthConsent(null);
        }}
        onCancel={() => {
          pendingOAuthConsent?.resolve(false);
          setPendingOAuthConsent(null);
        }}
      />

      <UsageLimitModal
        open={storageExceeded || limitState.open}
        mode={storageExceeded ? 'storage_exceeded' : limitState.mode}
        resetsAt={storageExceeded ? undefined : limitState.resetsAt}
        retryAfterSec={storageExceeded ? undefined : limitState.retryAfterSec}
        onUpgrade={() => {
          // Close the UsageLimitModal BEFORE opening the upgrade modal so the
          // <dialog> (which occupies the browser TOP LAYER) is dismissed first —
          // otherwise PlanChoiceModal (a z-index div) renders behind the open
          // <dialog> and the user is hard-trapped with no way to reach the upgrade UI.
          if (storageExceeded) {
            console.info('[usage-limit-modal] upgrade_clicked', { errorCode: 'STORAGE_LIMIT_EXCEEDED' });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'upgrade_clicked', errorCode: 'STORAGE_LIMIT_EXCEEDED' },
            }));
            setStorageExceeded(false);
          } else {
            console.info('[usage-limit-modal] upgrade_clicked', { errorCode: limitState.errorCode });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'upgrade_clicked', errorCode: limitState.errorCode },
            }));
            setLimitDismissed(true);
          }
          window.dispatchEvent(new CustomEvent('xavior:open-upgrade-modal'));
        }}
        onDismiss={() => {
          if (storageExceeded) {
            console.info('[usage-limit-modal] dismissed', { errorCode: 'STORAGE_LIMIT_EXCEEDED' });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'dismissed', errorCode: 'STORAGE_LIMIT_EXCEEDED' },
            }));
            setStorageExceeded(false);
          } else {
            console.info('[usage-limit-modal] dismissed', { errorCode: limitState.errorCode });
            window.dispatchEvent(new CustomEvent('xavior:usage-limit-modal', {
              detail: { action: 'dismissed', errorCode: limitState.errorCode },
            }));
            setLimitDismissed(true);
          }
        }}
      />

      {objectCardPopover && (
        <Suspense fallback={null}>
          <ContactCardPopover
            objectId={objectCardPopover.objectId}
            objectType={objectCardPopover.objectType}
            onClose={() => setObjectCardPopover(null)}
          />
        </Suspense>
      )}
      {docModal && docForEditor && (
        <Suspense fallback={null}>
          <DocEditorModal
            doc={docForEditor}
            onClose={() => { setDocModal(null); setDocForEditor(null); }}
          />
        </Suspense>
      )}
      {noteModal && (
        <NoteCardPopover
          noteId={noteModal.noteId}
          onClose={() => setNoteModal(null)}
          onOpenInModule={(id) => {
            applyUiActions([
              {
                type: 'module.focus',
                moduleType: 'notes',
                objectType: 'note',
                objectId: id,
                conversationId: activeConversationId,
              } as ChatUIAction,
            ]);
            setNoteModal(null);
          }}
        />
      )}
    </>
  );
}
